import { urls } from '~/preloaded';
import { GAME_TRANSACTION_STATUS } from '~/api/const';
import { post } from '~/helpers/api';
import { currentClanSelector } from '~/store/selectors/currentAccountSelector';
import { currentAccountActions } from '~/store/slices/currentAccountSlice';
import { syncAccountInfoThunk } from '~/store/thunks/accountThunks';
import { promiseWithGlobalSpinner } from '~/store/utils/globalSpinner';
import {
  sendClanDisbandNotification,
  sendClanLeaveErrorNotification,
  sendClanLeaveNotification,
} from '~/web2ClientAPI/notifications';

import type { InferActionsType } from '~/Reducers';
import type { AppAsyncThunk } from '~/store';
import type { IApiClanLeaveOrDisbandResponse } from '~/types/api';

export const CLAN_LEAVE_TOGGLE_FETCHING = 'CLAN_LEAVE_TOGGLE_FETCHING';
export const SELECT_COMMANDER = 'SELECT_COMMANDER';
export const SET_INITITAL_STATE = 'SET_INITITAL_STATE';

export type ActionsType = InferActionsType<typeof actionsClanLeave>;

export const actionsClanLeave = {
  toggleFetching: () =>
    ({
      type: CLAN_LEAVE_TOGGLE_FETCHING,
    }) as const,

  selectCommander: (newCommanderId: number) =>
    ({
      type: SELECT_COMMANDER,
      newCommanderId,
    }) as const,

  setInitialState: () =>
    ({
      type: SET_INITITAL_STATE,
    }) as const,
};

export const forceAccountSync =
  (isLeave: boolean): AppAsyncThunk =>
  async (dispatch, getState): Promise<void> => {
    const showLeaveClanNotification = () => {
      const state = getState();
      const clan = currentClanSelector(state);

      if (!clan) {
        return;
      }

      if (isLeave) {
        sendClanLeaveNotification(clan);
      } else {
        sendClanDisbandNotification(clan);
      }
    };

    try {
      const thunkPromise = dispatch(syncAccountInfoThunk({ leaveClan: showLeaveClanNotification }));
      await promiseWithGlobalSpinner(dispatch, thunkPromise);
    } catch {
      showLeaveClanNotification();
      dispatch(
        currentAccountActions.updateAccountInfo({
          clanId: null,
          roleName: null,
          activeInvitesCount: 0,
          activeApplicationsCount: 0,
          accumulativeClanResource: null,
          leveling: null,
          isBonusActivated: null,
        }),
      );
      dispatch(actionsClanLeave.setInitialState());
    }
  };

export const clanLeave = (): AppAsyncThunk<boolean> => async (dispatch) => {
  dispatch(actionsClanLeave.toggleFetching());

  try {
    const response = await post<IApiClanLeaveOrDisbandResponse>(urls.clanLeave);

    if (response?.error || response?.status === GAME_TRANSACTION_STATUS.FAILED || response?.additional_info) {
      const reason = response?.additional_info?.reason;

      if (reason === 'lead_cannot_leave_clan') {
        sendClanLeaveErrorNotification(reason);
        return false;
      }

      throw response;
    }

    return true;
  } catch (error) {
    sendClanLeaveErrorNotification();
    return false;
  } finally {
    dispatch(actionsClanLeave.toggleFetching());
  }
};

export const clanDisband = (): AppAsyncThunk<boolean> => async (dispatch) => {
  dispatch(actionsClanLeave.toggleFetching());

  try {
    const response = await post<IApiClanLeaveOrDisbandResponse>(urls.clanDisband);

    if (response?.error || response?.status === GAME_TRANSACTION_STATUS.FAILED) {
      throw response;
    }

    return true;
  } catch (error) {
    sendClanLeaveErrorNotification();
    return false;
  } finally {
    dispatch(actionsClanLeave.toggleFetching());
  }
};
